/**
 * LastFMGraph v3.4 - Modular Edition
 * Copyright © 2026 Andrew P E Collins All Rights Reserved
 * I'm 'acoil' on Last.FM. https://www.last.fm/user/acoil
 *
 * A data visualization to explore artis similarities based on
 * the listening data of given LastFM users.
 * Uses PIXI ( https://pixijs.com ) and ngraph ( https://github.com/anvaka/ngraph.graph ,
 * https://github.com/anvaka/ngraph.path ).
 *
 * Color palete from - https://www.lpi.usra.edu/resources/mapcatalog/usgs/I1162/
 *
 * Version 1.0 was a Flash app using Flex and Flare. It is now lost to history.
 *
 * Version 2.0 Was rewritten from scratch to use the ToxicLibs graph
 * library and P5js. It spent many years half finished but was finally
 * launched in late 2021.
 *
 * Version 3.0 moved from the old ToxicLibs graph lib to ngraph which
 * retains the organic spring-graph feel, but is much more efficient
 * and handles more nodes in the graph before getting bogged down.
 *
 * Version 3.1 Moved from P5js to PIXI for rendering in the summer of 2022.
 * There's a noticable improvement in drawing performance.
 * 
 * 3.2 Update to PIXI 8
 * 
 * 3.3 Added data on plays by DJs to highlight artists that have been played by DJs.
 * The idea is to be able to see what artists are similar to ones I've played
 * that Iggy Pop or John Peel have played.
 * 
 * 3.4 Moved to Vite based build system and split a monolithic .js file into
 * modules with help from Claude in early 2026. Zoom and pan was finally fixed.
 */

import * as PIXI from 'pixi.js';
import { BG_COLOR, FRAME_RATE, DJ_COLORS } from './config.js';
import { ArtistGraph } from './graph/graph.js';
import { AppState } from './state/store.js';
import { RequestQueue } from './utils/queue.js';
import { getDJs, getUser, getWeeklyArtistChart, getArtist } from './api/client.js';
import {
  handleUserResponse,
  handleWeeklyArtistChart,
  handleArtistResponse,
} from './data/handlers.js';
import { addUserDiv, highlightChartDiv, removeUserDiv, addDJLegend } from './ui/timeline.js';
import { Camera } from './rendering/camera.js';
import { computePlayRank, getTopUnlistenedArtists } from './graph/algorithms.js';

// Application state
const state = new AppState();
const queue = new RequestQueue();
let graph;
let app;
let lines, dots;
let w, h;
let camera;

/**
 * Initialize the application
 */
async function setup() {
  console.log('%cLastFMGraph v3.4 %cCopyright © 2026 Andrew P E Collins All Rights Reserved', 'color: red; font-weight: bold;', 'color: green');

  w = window.innerWidth;
  h = window.innerHeight;

  // Initialize PIXI application
  app = new PIXI.Application();
  await app.init({
    resizeTo: window,
    backgroundColor: BG_COLOR,
    antialias: true,
  });
  document.getElementById('canvasDiv').appendChild(app.canvas);

  // Initialize graph
  graph = new ArtistGraph();

  // Initialize camera (with viewport dimensions for proper centering)
  camera = new Camera(app.stage, w, h);
  camera.setupEventListeners(app.canvas);

  // Create containers for rendering
  lines = new PIXI.Container({ position: { x: 0, y: 0 }, interactiveChildren: false });
  dots = new PIXI.Container({ position: { x: 0, y: 0 }, eventMode: 'static' });

  // Setup event handlers
  setupEventHandlers();

  // Add containers to stage
  app.stage.addChild(lines, dots);
  app.ticker.maxFPS = FRAME_RATE;
  app.ticker.add(draw);

  // Load DJs
  await loadDJs();

  // Load initial users from URL or default
  loadInitialUsers();

  console.log('Application initialized');
}

/**
 * Setup DOM event handlers
 */
function setupEventHandlers() {
  // Note: Zoom and pan are handled by the Camera module

  // Pointer handlers for node dragging
  const moveNode = (e) => {
    // Don't move node if camera is panning
    if (camera.isPanningActive()) return;

    if (state.dragNode) {
      // Convert screen position to world position
      const worldPos = camera.screenToWorld(e.data.global.x, e.data.global.y);
      graph.setNodePosition(state.dragNode.id, worldPos.x, worldPos.y);
    }
  };

  const endMove = (e) => {
    if (state.dragNode) {
      if (
        Math.abs(state.dragStart.x - e.data.global.x) < 5 &&
        Math.abs(state.dragStart.y - e.data.global.y) < 5
      ) {
        graph.pinNode(state.dragNode, false);
        state.dragNode.data.pinned = false;
        state.dragNode.data.changed = true;
        state.newPinnedNode = true;
      }
      state.dragNode = undefined;
    }
  };

  dots
    .on('pointermove', moveNode)
    .on('pointerup', endMove)
    .on('globalpointermove', moveNode)
    .on('pointercancel', endMove);

  // User input handlers
  document.getElementById('userInput')?.addEventListener('keypress', (e) => {
    if (e.keyCode === 13) {
      addUserFromInput();
    }
  });

  document.getElementById('addUserButton')?.addEventListener('click', addUserFromInput);

  // Rank button handler
  document.getElementById('rankButton')?.addEventListener('click', handleRankButton);

  // Node search handler
  document.getElementById('nodeSearchText')?.addEventListener('input', handleNodeSearch);

  // About panel handlers
  document.getElementById('closeAboutButton')?.addEventListener('click', hideAbout);
  document.getElementById('showAboutButton')?.addEventListener('click', showAbout);

  if (localStorage.getItem('aboutHidden')) {
    hideAbout();
  }
}

/**
 * Main draw loop
 */
function draw(delta) {
  graph.step();
  drawGraph();
  queue.process();

  // Handle slow frames
  if (delta >= 6) {
    state.slowFrameCount += 1;
    if (state.slowFrameCount > 20) {
      console.log(`Slow frames detected. Nodes: ${graph.getNodeCount()}`);
      // TODO: Implement node pruning
      state.slowFrameCount = 0;
    }
  } else {
    state.slowFrameCount = Math.max(0, state.slowFrameCount - 1);
  }

  // Occasionally find shortest paths
  if (state.newPinnedNode || Math.random() > 0.995) {
    findShortestPaths();
  }
}

/**
 * Draw the graph
 */
function drawGraph() {
  // Draw edges
  graph.forEachLink((link) => {
    // Random edge highlighting
    if (Math.random() < 0.0005) {
      link.data.showCount = 30;
    }

    if (
      (link.data.showCount && link.data.showCount > 0) ||
      (link.data.onShortestPathCount && link.data.onShortestPathCount > 0)
    ) {
      const lp = graph.getLinkPosition(link.id);
      let line = link.data.line;

      if (!line) {
        line = new PIXI.Graphics();
        lines.addChild(line);
        link.data.line = line;
      }

      const lineColor = link.data.onShortestPathCount > 0 ? 0x101010 : 0xe1c1e8;

      if (link.data.showCount > 0) link.data.showCount -= 1;
      if (link.data.onShortestPathCount > 0) link.data.onShortestPathCount -= 1;

      if (
        (!link.data.showCount || link.data.showCount === 0) &&
        (!link.data.onShortestPathCount || link.data.onShortestPathCount === 0)
      ) {
        line.clear();
        line.destroy();
        link.data.line = undefined;
      } else {
        line.clear();
        line.moveTo(lp.from.x, lp.from.y);
        line.lineTo(lp.to.x, lp.to.y);
        line.stroke({ width: link.data.match * 4.5, color: lineColor, alpha: 0.7 });
      }
    }
  });

  // Draw nodes
  graph.forEachNode((node) => {
    const p = graph.getNodePosition(node.id);
    let circle = node.data.circle;

    if (!circle) {
      circle = new PIXI.Graphics();
      circle.eventMode = 'dynamic';
      circle.on('pointerdown', (e) => handleNodeClick(e, node));
      dots.addChild(circle);
      node.data.circle = circle;
      node.data.changed = true;
    }

    if (node.data.changed) {
      let r = 3;
      if (state.drawMode === 'playcount' && node.data.playcount) {
        r = r + Math.sqrt((node.data.playcount / state.maxPlaycount) * 300);
      } else if (state.drawMode === 'playRank' && node.data.playRank) {
        r = r + Math.sqrt((node.data.playRank / state.maxPlayRank) * 300);
      }

      circle.clear();
      if (node.data.pinned) {
        circle.circle(0, 0, r);
        circle.stroke({ width: 3, color: 0x050505 });
        circle.fill(node.data.color || 0x0097e6);
      } else {
        circle.circle(0, 0, r);
        circle.fill(node.data.color || 0x0097e6);
      }
      node.data.changed = false;
    }

    circle.x = p.x;
    circle.y = p.y;
  });

  // Draw labels
  graph.forEachNode((node) => {
    if (node.data.pinned) {
      node.data.labelFrameCount = Math.max(node.data.labelFrameCount || 0, 2);
    }

    if (node.data.labelFrameCount > 0) {
      const p = graph.getNodePosition(node.id);
      // Convert world position to screen position using camera
      const screenPos = camera.worldToScreen(p.x, p.y);
      node.data.labelFrameCount -= 1;

      let label = node.data.labelDiv;
      if (!label) {
        label = document.createElement('div');
        label.classList.add('nodeLabel');
        label.textContent = node.data.label || node.data.name;
        document.getElementById('canvasDiv').append(label);
        node.data.labelDiv = label;
      }

      label.setAttribute('style', `left: ${screenPos.x - 150}px; top: ${screenPos.y - 12}px`);

      if (node.data.labelFrameCount === 0) {
        label.remove();
        node.data.labelDiv = undefined;
      }
    }
  });
}

/**
 * Handle node click
 */
function handleNodeClick(e, node) {
  node.data.labelFrameCount = 100;
  graph.forEachLinkedNode(node.id, (linkedNode, link) => {
    link.data.showCount = 95 + Math.floor(Math.random() * 60);
  });
  graph.pinNode(node, true);
  node.data.pinned = true;
  node.data.changed = true;
  state.newPinnedNode = true;
  state.dragNode = node;
  state.dragStart = { ...e.data.global };
}

/**
 * Find shortest paths between pinned nodes
 */
function findShortestPaths() {
  graph.forEachLink((link) => {
    if (link.data.onShortestPathCount && link.data.onShortestPathCount > 1) {
      link.data.onShortestPathCount = 1;
    }
  });

  state.newPinnedNode = false;
  const pinnedNodes = [];

  graph.forEachNode((node) => {
    if (node.data.pinned) {
      pinnedNodes.push(node);
    }
  });

  if (pinnedNodes.length < 2) return;

  for (let i = 0; i < pinnedNodes.length; i += 1) {
    for (let j = i + 1; j < pinnedNodes.length; j += 1) {
      const fromNode = pinnedNodes[i];
      const toNode = pinnedNodes[j];
      const path = graph.findPath(fromNode.id, toNode.id);

      path.forEach((node) => {
        node.data.labelFrameCount = 120 + Math.floor(90 * Math.random());
      });

      for (let k = 1; k < path.length; k += 1) {
        let link = graph.getLink(path[k - 1].id, path[k].id);
        if (!link) {
          link = graph.getLink(path[k].id, path[k - 1].id);
        }
        if (link) {
          link.data.onShortestPathCount = 200;
        }
      }
    }
  }
}

/**
 * Load DJs from API
 */
async function loadDJs() {
  try {
    const data = await getDJs();
    data.djs.forEach((dj, i) => {
      dj.color = DJ_COLORS[i % DJ_COLORS.length];
    });
    state.setDJs(data.djs);
    console.log('DJs loaded:', state.djs.length);

    // Display DJ legend
    addDJLegend(state.djs);
  } catch (error) {
    console.error('Error loading DJs:', error);
  }
}

/**
 * Load initial users from URL parameters
 */
function loadInitialUsers() {
  const sp = new URLSearchParams(window.location.search);
  if (sp.has('users')) {
    sp.get('users').split(',').forEach((username) => {
      loadUser(username);
    });
  } else {
    loadUser('acoil');
  }
}

/**
 * Load a user and their data
 */
async function loadUser(username) {
  if (state.findUser(username)) {
    console.log(`User ${username} already loaded`);
    return;
  }

  try {
    const json = await getUser(username);
    const result = handleUserResponse(json, state, queueCharts);

    if (result.error) {
      document.getElementById('addUserMessage').textContent = result.error;
      return;
    }

    addUserDiv(result, handleRemoveUser);
    console.log(`User ${username} loaded`);
  } catch (error) {
    console.error(`Error loading user ${username}:`, error);
    document.getElementById('addUserMessage').textContent = `Error: ${error.message}`;
  }
}

/**
 * Queue charts for loading
 */
function queueCharts(user) {
  const charts = user.charts.filter((c) => c.date >= state.startDate);

  charts.forEach((c) => {
    const url = new URL(`weeklyartistchart/${c.id}`, getApiBase());
    queue.add('charts', {
      url: url,
      date: c.date,
      handler: (json) => {
        const result = handleWeeklyArtistChart(json, state, graph, queueArtistDetails);
        if (result.chart) {
          highlightChartDiv(result.chart);
        }
      },
    });
  });

  // Sort descending so .pop() processes oldest first (left to right on timeline).
  // Stores date on each entry so the sort works across multiple users.
  queue.queues.charts.sort((a, b) => b.date - a.date);
}

/**
 * Queue artist details for loading
 */
function queueArtistDetails(artistId) {
  const url = new URL(`artist/${artistId}`, getApiBase());
  queue.add('artists', {
    url: url,
    handler: (json) => {
      handleArtistResponse(json, state, graph, w, h);
    },
  });
}

/**
 * Get API base URL
 */
function getApiBase() {
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    return 'http://127.0.0.1:8000/';
  }
  return 'https://www.atomodo.com/lfmg/api/';
}

/**
 * Add user from input field
 */
function addUserFromInput() {
  const input = document.getElementById('userInput');
  const username = input.value.trim();

  if (!username) return;

  if (state.findUser(username)) {
    document.getElementById('addUserMessage').textContent = 'User already added.';
    return;
  }

  loadUser(username);
  input.value = '';
}

/**
 * Handle user removal
 */
function handleRemoveUser(userId) {
  const user = state.users.find((u) => u.id === userId);
  if (!user) return;

  removeUserDiv(user.name);
  state.removeUser(userId);

  // Clear and reload
  graph.clear();
  queue.clear();
  state.users.forEach((u) => queueCharts(u));
}

/**
 * Handle rank button click
 */
function handleRankButton() {
  const rankButton = document.getElementById('rankButton');

  if (rankButton.value === 'rank') {
    rankButton.value = 'count';

    // Compute play rank for all nodes
    computePlayRank(graph, state);

    // Switch to rank display mode
    state.toggleDrawMode();
    graph.forEachNode((node) => {
      node.data.changed = true;
    });

    // Show top unlistened artists
    showTopUnlistenedArtists();

    console.log('Switched to rank mode');
  } else {
    rankButton.value = 'rank';
    state.toggleDrawMode();
    graph.forEachNode((node) => {
      node.data.changed = true;
    });
    document.getElementById('topUnlistenedDiv')?.classList.add('hidden');
    console.log('Switched to playcount mode');
  }
}

/**
 * Display top unlistened artists based on play rank
 */
function showTopUnlistenedArtists() {
  const topUnlistened = getTopUnlistenedArtists(state.artists, 10);
  const topUnlistenedDiv = document.getElementById('topUnlistenedDiv');
  const topUnlistenedList = document.getElementById('topUnlistenedList');

  if (!topUnlistenedList) return;

  topUnlistenedList.innerHTML = ''; // Clear previous list

  topUnlistened.forEach((artist) => {
    const li = document.createElement('li');
    li.textContent = `${artist.name} (${artist.playRank.toFixed(3)})`;
    topUnlistenedList.appendChild(li);
  });

  topUnlistenedDiv?.classList.remove('hidden');
}

/**
 * Handle node search by artist name
 */
function handleNodeSearch(e) {
  const searchText = e.target.value.trim().toLowerCase();

  // Need at least 3 characters to search
  if (searchText.length < 3) {
    return;
  }

  // Find matching nodes
  const matches = [];
  graph.forEachNode((node) => {
    if (node.data.name && node.data.name.toLowerCase().includes(searchText)) {
      matches.push(node);
    }
  });

  // If fewer than 10 matches, show their labels
  if (matches.length > 0 && matches.length < 10) {
    matches.forEach((node) => {
      node.data.labelFrameCount = 300;
    });
  }
}

/**
 * Hide about panel
 */
function hideAbout() {
  const aboutDiv = document.getElementById('aboutDiv');
  aboutDiv?.classList.add('transparent');
  const showButton = document.getElementById('showAboutButton');
  showButton?.classList.remove('hidden');
  showButton?.classList.add('transparent');
  setTimeout(() => {
    aboutDiv?.classList.add('hidden');
    showButton?.classList.remove('transparent');
  }, 2000);
  localStorage.setItem('aboutHidden', 'true');
}

/**
 * Show about panel
 */
function showAbout() {
  const aboutDiv = document.getElementById('aboutDiv');
  aboutDiv?.classList.remove('hidden');
  aboutDiv?.classList.remove('transparent');
  const showButton = document.getElementById('showAboutButton');
  showButton?.classList.add('transparent');
  setTimeout(() => {
    showButton?.classList.add('hidden');
  }, 2000);
  localStorage.setItem('aboutHidden', 'false');
}

// Start the application
setup().catch(console.error);
